import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTrustFlags } from '../../features/career-trust/hooks/useTrustFlags';
import type { TrustFeatureFlagKey } from '../../shared/lib/trustFlags';
import Loader from '../../shared/components/common/Loader';

interface TrustFeatureRouteProps {
  flag: TrustFeatureFlagKey;
  redirectTo?: string;
}

function inferDashboardRoot(pathname: string): string {
  if (pathname.startsWith('/referral-dashboard/jobseeker-dashboard')) {
    return '/referral-dashboard/jobseeker-dashboard';
  }
  if (pathname.startsWith('/referral-dashboard')) {
    return '/referral-dashboard';
  }
  return '/jobseeker-dashboard';
}

/**
 * Route guard for Career Trust surfaces.
 *
 * Unlike the platform FeatureRoute (which fails open while loading), this one
 * fails CLOSED: while the flag state is unknown it shows a loader rather than the
 * gated content, and it redirects if the flag is off. Trust surfaces are disabled
 * by default, so briefly rendering them before a redirect would be the wrong bias.
 */
export function TrustFeatureRoute({ flag, redirectTo }: TrustFeatureRouteProps) {
  const { isEnabled, loading } = useTrustFlags();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem 0' }}>
        <Loader />
      </div>
    );
  }

  if (!isEnabled(flag)) {
    return <Navigate to={redirectTo ?? inferDashboardRoot(location.pathname)} replace />;
  }

  return <Outlet />;
}

export default TrustFeatureRoute;
